// ─────────────────────────────────────────────────────────
// Einstellungen → Layout & Textvorlagen → Briefpapier (plancraft-Modell)
// ─────────────────────────────────────────────────────────
// Phase 1: Tabs (Dokumente/Berichte), Briefkopf (Unternehmen, Kontaktdaten,
// Absenderzeile, Logo, Ansprechperson) + Fußzeile (Rechtliches, Bank).
// Phase 2a: Layout (Seitenabstände, Schriftart) — liegt auf der Organization,
// deshalb eigener Endpunkt (/api/letterhead/layout) statt CompanyProfile-Patch.
// Jeder Abschnitt ist ein Modal und speichert PARTIELL (nur seine Felder).
// Prefix lh-. Rechte: layout:read / layout:write.
// Sprache: alle sichtbaren Texte über t() — Keys in i18n/de.json + en.json.
// `uploadHint` enthält bewusst Markup (<br>/<strong>) und wird NICHT escaped: ein Satz,
// der in Fragmente zerlegt wird, friert die WORTSTELLUNG der Ausgangssprache ein
// („PDF oder Bild | im A4 Format | hochladen" — im Englischen steht das Verb vorne).
// Jede Sprache bestimmt Reihenfolge und Umbrüche selbst. Unescaped ist zulässig, weil
// die Sprachdateien zur Bauzeit gebündelte Konstanten sind — nie Nutzereingaben.
// Ausnahme: Briefkopf-/Fußzeilen-INHALTE und ihre Beispiel-Platzhalter (Firmenname,
// Adresse, IBAN, Beispiel-E-Mail/-Telefon) bleiben Deutsch — sie spiegeln, was am
// Ende auf dem PDF beim Kunden landet (Kundentext ≠ UI-Sprache). Schriftart-Namen
// sind Eigennamen. mm-Einheiten werden nicht übersetzt.
import { icon, escapeHtml } from '../../utils/dom.js';
import {
  saveLetterhead, saveContactPersons, getLetterheadPreview, saveLetterheadLayout, saveDocumentLayout,
  uploadLetterheadBackground, deleteLetterheadBackground, fetchLetterheadBackgroundBlob,
} from '../../core/org.js';
import { fetchCompanyLogoBlob } from '../../core/documents.js';
import { t } from '../../i18n/index.js';
import { fetchMe } from '../../core/auth.js';

// ── Live-Vorschau: Muster-Offerte, mit demselben Renderer wie das echte PDF ──
export function loadLetterheadPreview(ctx) {
  const { s, draw } = ctx;
  if (s.lhPreviewLoading) return;
  s.lhPreviewLoading = true;
  getLetterheadPreview()
    .then((r) => { s.lhPreview = r.html || ''; })
    .catch(() => { s.lhPreview = ''; })
    .finally(() => { s.lhPreviewLoading = false; draw(); });
}

// Nur die Codes — das Label kommt bei jedem Render über t(), sonst friert es in der
// Startsprache ein (Modul-Level-Konstante darf keinen übersetzten Text enthalten).
const COUNTRY_CODES = ['CH', 'DE', 'AT', 'LI'];

function data(s) { return s.lhData || {}; }
function lh(s) { return data(s).letterhead || {}; }
function layout(s) { return data(s).layout || { font: '', margins: {}, background: {} }; }

// ── Tabs + Karten ─────────────────────────────────────────
export function renderLetterheadSection(ctx) {
  const { s, can } = ctx;
  if (!can('layout:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  const tab = s.lhTab || 'dokumente';
  const w = can('layout:write');

  const tabs = `
    <div class="cd-lh-tabs">
      <button class="cd-lh-tab ${tab === 'dokumente' ? 'active' : ''}" data-action="lh-tab" data-tab="dokumente">${escapeHtml(t('settings.letterhead.tabDocuments'))}</button>
      <button class="cd-lh-tab ${tab === 'berichte' ? 'active' : ''}" data-action="lh-tab" data-tab="berichte">${escapeHtml(t('settings.letterhead.tabReports'))}</button>
    </div>`;

  if (tab === 'berichte') {
    return `${tabs}<div class="cd-set-empty">${icon('description', 40)}<p>${escapeHtml(t('settings.letterhead.reportsComingSoon'))}</p></div>`;
  }

  const btn = (action, ic, label) =>
    `<button class="cd-lh-btn" data-action="${action}" ${w ? '' : 'disabled'}>${icon(ic, 18)}${escapeHtml(label)}</button>`;

  const l = layout(s);
  const auto = l.mode !== 'CUSTOM';

  // ── Layout-Modus: automatisch generiertes Briefpapier vs. eigener Briefbogen ──
  const modeRadio = (value, label) => `
    <label class="cd-lh-radio">
      <input type="radio" name="lh-mode" value="${value}" data-action="lh-mode" ${l.mode === value ? 'checked' : ''} ${w ? '' : 'disabled'}>
      <span>${escapeHtml(label)}</span>
    </label>`;

  const layoutCard = `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('tune', 18)}<h3>${escapeHtml(t('settings.letterhead.layoutTitle'))}</h3></div>
      <div class="cd-lh-radios">
        ${modeRadio('AUTO', t('settings.letterhead.modeAuto'))}
        ${modeRadio('CUSTOM', t('settings.letterhead.modeCustom'))}
      </div>
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span><strong>${escapeHtml(auto ? t('settings.letterhead.modeAuto') : t('settings.letterhead.modeCustom'))}:</strong> ${escapeHtml(auto
        ? t('settings.letterhead.autoDesc')
        : t('settings.letterhead.customDesc'))}</span></div>
    </div>`;

  // ── PDF-Gestaltung der Offerte: Standard (Positionstabelle) vs. Umzug
  //    (Buchungsbestätigung mit Servicedetails + Inventar). Speichert sofort. ──
  const docLayout = data(s).documentLayout || 'STANDARD';
  const docLayoutRadio = (value, label) => `
    <label class="cd-lh-radio">
      <input type="radio" name="lh-doclayout" value="${value}" data-action="lh-doclayout" ${docLayout === value ? 'checked' : ''} ${w ? '' : 'disabled'}>
      <span>${escapeHtml(label)}</span>
    </label>`;
  const docLayoutCard = `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('dashboard', 18)}<h3>${escapeHtml(t('settings.letterhead.docLayoutTitle'))}</h3></div>
      <div class="cd-lh-radios">
        ${docLayoutRadio('STANDARD', t('settings.letterhead.docLayoutStandard'))}
        ${docLayoutRadio('UMZUG', t('settings.letterhead.docLayoutUmzug'))}
      </div>
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>${escapeHtml(docLayout === 'UMZUG'
        ? t('settings.letterhead.docLayoutUmzugDesc')
        : t('settings.letterhead.docLayoutStandardDesc'))}</span></div>
    </div>`;

  // ── Individueller Hintergrund (beide Modi) + Seitenabstände ──
  const slotBox = (slot, hint) => {
    const has = !!(l.background || {})[slot];
    const preview = s.lhBgPreview?.[slot];
    return `
      <div class="cd-lh-bg-slot">
        ${has
          ? `<div class="cd-lh-bg-filled" data-action="lh-bg-pick" data-slot="${slot}">
               ${preview ? `<img src="${escapeHtml(preview)}" alt="${escapeHtml(t('settings.letterhead.backgroundAlt'))}">` : '<div class="cd-lh-bg-loading"></div>'}
               ${w ? `<button class="cd-lh-bg-del" data-action="lh-bg-del" data-slot="${slot}" title="${escapeHtml(t('settings.letterhead.remove'))}">${icon('delete', 16)}</button>` : ''}
             </div>`
          : `<div class="cd-lh-bg-empty" data-action="lh-bg-pick" data-slot="${slot}">
               ${hint ? `<div class="cd-lh-bg-hint">${escapeHtml(hint)}</div>` : ''}
               <div>${t('settings.letterhead.uploadHint')}</div>
             </div>`}
      </div>`;
  };

  const bgCard = `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('wallpaper', 18)}<h3>${escapeHtml(t('settings.letterhead.customBackgroundTitle'))}</h3></div>
      <div class="cd-lh-bg-row">
        ${slotBox('first', '')}
        ${slotBox('rest', t('settings.letterhead.bgHintRest'))}
      </div>
      ${s.lhBgUploading ? `<div class="cd-lh-bg-status">${escapeHtml(t('settings.letterhead.uploading'))}</div>` : ''}
      ${s.lhBgError ? `<div class="cd-un-err">${escapeHtml(s.lhBgError)}</div>` : ''}
      <div class="cd-lh-btns">
        ${btn('lh-edit-abstaende', 'height', t('settings.letterhead.editMargins'))}
      </div>
    </div>`;

  // ── Schriftart: Select direkt auf der Karte (kein Modal) ──
  const fontCard = `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('text_fields', 18)}<h3>${escapeHtml(t('settings.letterhead.fontTitle'))}</h3></div>
      <div class="cd-lh-fontpick">
        <div class="cd-select-shell">
          <select class="cd-input" id="lh-font" ${w ? '' : 'disabled'}>
            ${(data(s).fonts || []).map((f) => `<option value="${escapeHtml(f.value)}" ${l.font === f.value ? 'selected' : ''}>${escapeHtml(f.label)}${f.value === 'Roboto' ? ' ' + escapeHtml(t('settings.letterhead.defaultSuffix')) : ''}</option>`).join('')}
          </select>${icon('expand_more', 18)}
        </div>
      </div>
    </div>`;

  const preview = `
    <aside class="cd-lh-preview">
      <div class="cd-lh-preview-head">${escapeHtml(t('settings.letterhead.previewTitle'))}</div>
      <div class="cd-lh-preview-frame">
        ${s.lhPreviewLoading
          ? `<div class="cd-muted" style="padding:16px;">${escapeHtml(t('common.loading'))}</div>`
          : (s.lhPreview
            ? `<iframe class="cd-lh-preview-iframe" sandbox="" srcdoc="${escapeHtml(s.lhPreview)}" title="${escapeHtml(t('settings.letterhead.previewIframeTitle'))}"></iframe>`
            : `<div class="cd-muted" style="padding:16px;">${escapeHtml(t('settings.letterhead.noPreview'))}</div>`)}
      </div>
    </aside>`;

  // Beim eigenen Briefbogen stehen Firmenblock/Logo/Fußzeile schon auf dem Papier
  // → nur die Felder anbieten, die LevelUpWerk trotzdem aufs Blatt schreibt.
  const briefkopfCard = `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('description', 18)}<h3>${escapeHtml(t('settings.letterhead.headerTitle'))}</h3></div>
      <div class="cd-lh-btns">
        ${auto ? btn('lh-edit-unternehmen', 'apartment', t('settings.letterhead.editCompany')) : ''}
        ${auto ? btn('lh-edit-kontakt', 'contact_page', t('settings.letterhead.editContact')) : ''}
        ${btn('lh-edit-absender', 'mail', t('settings.letterhead.editSenderLine'))}
        ${auto ? btn('lh-edit-logo', 'image', t('settings.letterhead.editLogo')) : ''}
        ${btn('lh-edit-person', 'account_circle', t('settings.letterhead.editContactPerson'))}
      </div>
    </div>`;

  const fusszeileCard = auto ? `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('article', 18)}<h3>${escapeHtml(t('settings.letterhead.footerTitle'))}</h3></div>
      <div class="cd-lh-btns">
        ${btn('lh-edit-rechtliches', 'gavel', t('settings.letterhead.editLegal'))}
        ${btn('lh-edit-bank', 'account_balance', t('settings.letterhead.editBank'))}
      </div>
    </div>` : '';

  return `
    ${tabs}
    <div class="cd-lh-split">
    <div class="cd-emp">
      ${layoutCard}
      ${docLayoutCard}
      ${bgCard}
      ${briefkopfCard}
      ${fusszeileCard}
      ${fontCard}
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>${escapeHtml(auto
        ? t('settings.letterhead.autoNote')
        : t('settings.letterhead.customNote'))}</span></div>
    </div>
    ${preview}
    </div>`;
}

// ── DOM → Entwurf (vor Redraw/Save, damit Eingaben erhalten bleiben) ──
export function syncFromDom(s) {
  const e = s.lhEdit;
  if (!e) return;
  const val = (id) => document.getElementById(id)?.value ?? '';
  const checked = (id) => !!document.getElementById(id)?.checked;

  if (e.section === 'unternehmen') {
    e.data.legalName = val('lh-legalName'); e.data.street = val('lh-street');
    e.data.addressSuffix = val('lh-addressSuffix'); e.data.postalCode = val('lh-postalCode');
    e.data.city = val('lh-city'); e.data.country = val('lh-country'); e.data.trade = val('lh-trade');
  } else if (e.section === 'kontakt') {
    e.data.contactEmails = e.data.contactEmails.map((_, i) => ({ value: val(`lh-em-v-${i}`), label: val(`lh-em-l-${i}`) }));
    e.data.contactPhones = e.data.contactPhones.map((_, i) => ({ value: val(`lh-ph-v-${i}`), label: val(`lh-ph-l-${i}`) }));
    e.data.website = val('lh-website');
  } else if (e.section === 'absender') {
    e.data.senderLineShow = checked('lh-senderLineShow');
    e.data.senderLineAddress = val('lh-senderLineAddress');
  } else if (e.section === 'rechtliches') {
    e.data.legalResponsibleType = val('lh-legalType');
    e.data.legalResponsiblePersons = e.data.legalResponsiblePersons.map((_, i) => val(`lh-person-${i}`));
    e.data.uid = val('lh-uid'); e.data.vatNumber = val('lh-vatNumber');
    e.data.commercialRegisterNumber = val('lh-hrNumber'); e.data.commercialRegisterOffice = val('lh-hrOffice');
  } else if (e.section === 'bank') {
    e.data.bankAccounts = e.data.bankAccounts.map((_, i) => ({
      bankName: val(`lh-bank-name-${i}`), accountHolder: val(`lh-bank-holder-${i}`),
      iban: val(`lh-bank-iban-${i}`), bic: val(`lh-bank-bic-${i}`),
    }));
  } else if (e.section === 'person') {
    e.data.contactPersons = e.data.contactPersons.map((p, i) => ({
      ...p, email: val(`lh-cp-em-${i}`), phone: val(`lh-cp-ph-${i}`),
    }));
  } else if (e.section === 'abstaende') {
    e.data.margins = {
      topFirst: val('lh-m-topFirst'), topFollowing: val('lh-m-topFollowing'), bottom: val('lh-m-bottom'),
    };
  }
}

// ── Modal-Hülle ───────────────────────────────────────────
function modalShell(title, ic, bodyHtml, s, wide) {
  return `
    <div class="cd-emp-modal-overlay" data-action="lh-cancel">
      <div class="cd-emp-modal cd-lh-modal ${wide ? 'wide' : ''}" data-action="lh-stop">
        <div class="cd-emp-modal-head">
          <h2>${icon(ic, 20)}${escapeHtml(title)}</h2>
          <div class="cd-set-head-actions">
            <button class="cd-btn-ghost" data-action="lh-cancel">${escapeHtml(t('common.cancel'))}</button>
            <button class="cd-btn-primary" data-action="lh-save" ${s.lhSaving ? 'disabled' : ''}>${escapeHtml(s.lhSaving ? t('common.saving') : t('common.save'))}</button>
          </div>
        </div>
        <div class="cd-emp-modal-body">${bodyHtml}</div>
      </div>
    </div>`;
}
const field = (label, id, value, ph = '', type = 'text') => `
  <div class="cd-dt-field">
    <label class="cd-dt-cap" for="${id}">${escapeHtml(label)}</label>
    <input class="cd-input" id="${id}" type="${type}" value="${escapeHtml(value ?? '')}" placeholder="${escapeHtml(ph)}">
  </div>`;
const selectField = (label, id, value, opts) => `
  <div class="cd-dt-field">
    <label class="cd-dt-cap" for="${id}">${escapeHtml(label)}</label>
    <div class="cd-select-shell"><select class="cd-input" id="${id}">
      ${opts.map(([v, l]) => `<option value="${escapeHtml(v)}" ${value === v ? 'selected' : ''}>${escapeHtml(l)}</option>`).join('')}
    </select>${icon('expand_more', 18)}</div>
  </div>`;

const mmField = (label, id, value, hint) => `
  <div class="cd-dt-field">
    <label class="cd-dt-cap" for="${id}">${escapeHtml(label)}</label>
    <div class="cd-lh-mm">
      <input class="cd-input" id="${id}" type="number" min="0" max="100" step="0.5" value="${escapeHtml(String(value ?? ''))}">
      <span class="cd-lh-mm-unit">mm</span>
    </div>
    ${hint ? `<div class="cd-lh-hint">${escapeHtml(hint)}</div>` : ''}
  </div>`;

export function renderLetterheadModal(ctx) {
  const { s } = ctx;
  const e = s.lhEdit;
  if (!e) return '';
  const d = e.data;

  if (e.section === 'abstaende') {
    return modalShell(t('settings.letterhead.marginsTitle'), 'height', `
      ${mmField(t('settings.letterhead.marginTopFirst'), 'lh-m-topFirst', d.margins.topFirst, t('settings.letterhead.marginTopFirstHint'))}
      ${mmField(t('settings.letterhead.marginTopFollowing'), 'lh-m-topFollowing', d.margins.topFollowing, t('settings.letterhead.marginTopFollowingHint'))}
      ${mmField(t('settings.letterhead.marginBottom'), 'lh-m-bottom', d.margins.bottom, t('settings.letterhead.marginBottomHint'))}
      <div class="cd-emp-info cd-sc-note" style="margin:8px 0 0;">${icon('info', 18)}<span>${escapeHtml(t('settings.letterhead.sideMarginNote', { value: String(layout(s).sideMargin ?? 18) }))}</span></div>`, s);
  }


  if (e.section === 'unternehmen') {
    const trades = [['', t('settings.letterhead.tradeSelect')]].concat((data(s).trades || []).map((trade) => [trade, trade]));
    const countries = COUNTRY_CODES.map((c) => [c, t(`settings.letterhead.country.${c}`)]);
    return modalShell(t('settings.letterhead.companyTitle'), 'apartment', `
      ${field(t('settings.letterhead.companyLegalName'), 'lh-legalName', d.legalName, 'Muster Handwerk GmbH')}
      ${field(t('settings.letterhead.companyStreet'), 'lh-street', d.street, 'Bahnhofstrasse 3')}
      ${field(t('settings.letterhead.companyAddressSuffix'), 'lh-addressSuffix', d.addressSuffix, 'c/o, Gebäude …')}
      <div class="cd-lh-row2">
        ${field(t('settings.letterhead.companyPostalCode'), 'lh-postalCode', d.postalCode, '8001')}
        ${field(t('settings.letterhead.companyCity'), 'lh-city', d.city, 'Zürich')}
      </div>
      ${selectField(t('settings.letterhead.companyCountry'), 'lh-country', d.country || 'CH', countries)}
      ${selectField(t('settings.letterhead.companyTrade'), 'lh-trade', d.trade || '', trades)}`, s);
  }

  if (e.section === 'kontakt') {
    const rows = (list, kind, addAction, label) => `
      <label class="cd-dt-cap">${escapeHtml(label)}</label>
      ${list.map((c, i) => `
        <div class="cd-lh-contact">
          <input class="cd-input" id="lh-${kind}-v-${i}" type="text" value="${escapeHtml(c.value || '')}" placeholder="${kind === 'em' ? 'info@firma.ch' : '+41 44 111 22 33'}">
          <input class="cd-input cd-lh-label" id="lh-${kind}-l-${i}" type="text" value="${escapeHtml(c.label || '')}" placeholder="${escapeHtml(kind === 'em' ? t('settings.letterhead.email') : t('settings.letterhead.phone'))}">
          <button class="cd-dt-del" data-action="lh-del-item" data-list="${kind === 'em' ? 'contactEmails' : 'contactPhones'}" data-index="${i}" title="${escapeHtml(t('settings.letterhead.remove'))}">${icon('close', 16)}</button>
        </div>`).join('')}
      <button class="cd-dt-add" data-action="lh-add-item" data-list="${kind === 'em' ? 'contactEmails' : 'contactPhones'}">${escapeHtml(addAction)}</button>`;
    return modalShell(t('settings.letterhead.contactTitle'), 'contact_page', `
      <div class="cd-lh-group">${rows(d.contactEmails, 'em', t('settings.letterhead.addEmail'), t('settings.letterhead.email'))}</div>
      <div class="cd-lh-group">${rows(d.contactPhones, 'ph', t('settings.letterhead.addPhone'), t('settings.letterhead.phone'))}</div>
      ${field(t('settings.letterhead.companyWebsite'), 'lh-website', d.website, 'www.firma.ch')}`, s);
  }

  if (e.section === 'absender') {
    return modalShell(t('settings.letterhead.senderLineTitle'), 'mail', `
      <label class="cd-lh-check"><input type="checkbox" id="lh-senderLineShow" data-action="lh-toggle-sender" ${d.senderLineShow ? 'checked' : ''}> ${escapeHtml(t('settings.letterhead.senderLineShow'))}</label>
      ${d.senderLineShow ? field(t('settings.letterhead.senderLineAddress'), 'lh-senderLineAddress', d.senderLineAddress, 'Muster GmbH | Bahnhofstrasse 3 | 8001 Zürich') : ''}
      <div class="cd-emp-info cd-sc-note" style="margin:8px 0 0;">${icon('info', 18)}<span>${escapeHtml(t('settings.letterhead.senderLineNote'))}</span></div>`, s);
  }

  if (e.section === 'logo') {
    const preview = s.logoPreview || (lh(s).logoUrl ? lh(s).logoUrl : '');
    return modalShell(t('settings.letterhead.logoTitle'), 'image', `
      <div class="cd-lh-logo-box" data-action="upload-logo">
        ${preview ? `<img class="cd-lh-logo-img" src="${escapeHtml(preview)}" alt="${escapeHtml(t('settings.letterhead.logoAlt'))}">` : `<div class="cd-lh-logo-empty"><div>${escapeHtml(t('settings.letterhead.logoUploadHint'))}</div><div class="cd-muted">${escapeHtml(t('settings.letterhead.logoMaxSize'))}</div></div>`}
      </div>
      ${s.logoUploading ? `<div class="cd-muted" style="padding:0 16px;">${escapeHtml(t('settings.letterhead.uploading'))}</div>` : ''}
      ${s.logoError ? `<div class="cd-un-err">${escapeHtml(s.logoError)}</div>` : ''}
      <div class="cd-emp-info cd-sc-note" style="margin:8px 0 0;">${icon('info', 18)}<span>${escapeHtml(t('settings.letterhead.logoNote'))}</span></div>`, s);
  }

  if (e.section === 'person') {
    const rows = d.contactPersons.map((p, i) => `
      <tr>
        <td class="cd-lh-cp-name">${escapeHtml(p.name || '')}</td>
        <td><input class="cd-input" id="lh-cp-em-${i}" type="text" value="${escapeHtml(p.email || '')}" placeholder="${escapeHtml(p.emailFallback || t('settings.letterhead.email'))}"></td>
        <td><input class="cd-input" id="lh-cp-ph-${i}" type="text" value="${escapeHtml(p.phone || '')}" placeholder="${escapeHtml(p.phoneFallback || t('settings.letterhead.phone'))}"></td>
      </tr>`).join('');
    return modalShell(t('settings.letterhead.personTitle'), 'account_circle', `
      <table class="cd-lh-cp">
        <thead><tr><th>${escapeHtml(t('common.name'))}</th><th>${escapeHtml(t('settings.letterhead.email'))}</th><th>${escapeHtml(t('settings.letterhead.phone'))}</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="cd-emp-info cd-sc-note" style="margin:10px 0 0;">${icon('info', 18)}<span>${escapeHtml(t('settings.letterhead.personNote'))}</span></div>`, s, true);
  }

  if (e.section === 'rechtliches') {
    const types = [['', t('settings.letterhead.selectGeneric')]].concat((data(s).legalResponsibleTypes || []).map((typ) => [typ, typ]));
    return modalShell(t('settings.letterhead.legalTitle'), 'gavel', `
      ${selectField(t('settings.letterhead.legalResponsibleType'), 'lh-legalType', d.legalResponsibleType || '', types)}
      <label class="cd-dt-cap">${escapeHtml(t('settings.letterhead.legalResponsiblePerson'))}</label>
      ${d.legalResponsiblePersons.map((p, i) => `
        <div class="cd-dt-block">
          <input class="cd-input cd-dt-block-in" id="lh-person-${i}" type="text" value="${escapeHtml(p)}" placeholder="${escapeHtml(t('common.name'))}">
          <button class="cd-dt-del" data-action="lh-del-item" data-list="legalResponsiblePersons" data-index="${i}" title="${escapeHtml(t('settings.letterhead.remove'))}">${icon('close', 16)}</button>
        </div>`).join('')}
      <button class="cd-dt-add" data-action="lh-add-item" data-list="legalResponsiblePersons">${escapeHtml(t('settings.letterhead.addLegalResponsiblePerson'))}</button>
      <div style="height:10px"></div>
      ${field(t('settings.letterhead.uid'), 'lh-uid', d.uid, 'CHE-123.456.789')}
      ${field(t('settings.letterhead.vatNumber'), 'lh-vatNumber', d.vatNumber, 'CHE-123.456.789 MWST')}
      ${field(t('settings.letterhead.commercialRegisterNumber'), 'lh-hrNumber', d.commercialRegisterNumber, 'CH-020.4.123.456-7')}
      ${field(t('settings.letterhead.commercialRegisterOffice'), 'lh-hrOffice', d.commercialRegisterOffice, 'Handelsregisteramt Zürich')}`, s);
  }

  if (e.section === 'bank') {
    const rows = d.bankAccounts.map((b, i) => `
      <div class="cd-lh-group">
        <div class="cd-lh-grouphead">
          <span class="cd-nr-cap">${escapeHtml(t('settings.letterhead.bankAccountLabel', { index: i + 1 }))}</span>
          <button class="cd-dt-del" data-action="lh-del-item" data-list="bankAccounts" data-index="${i}" title="${escapeHtml(t('settings.letterhead.remove'))}">${icon('close', 16)}</button>
        </div>
        ${field(t('settings.letterhead.bankName'), `lh-bank-name-${i}`, b.bankName, 'Zürcher Kantonalbank')}
        ${field(t('settings.letterhead.bankAccountHolder'), `lh-bank-holder-${i}`, b.accountHolder, 'Muster Handwerk GmbH')}
        <div class="cd-lh-row2">
          ${field(t('settings.letterhead.bankIban'), `lh-bank-iban-${i}`, b.iban, 'CH93 0076 2011 6238 5295 7')}
          ${field(t('settings.letterhead.bankBic'), `lh-bank-bic-${i}`, b.bic, 'ZKBKCHZZ80A')}
        </div>
      </div>`).join('');
    return modalShell(t('settings.letterhead.bankTitle'), 'account_balance', `
      ${rows || `<div class="cd-muted" style="padding:0 16px 8px;">${escapeHtml(t('settings.letterhead.noBankAccount'))}</div>`}
      <button class="cd-dt-add" data-action="lh-add-item" data-list="bankAccounts">${escapeHtml(t('settings.letterhead.addBank'))}</button>
      <div class="cd-emp-info cd-sc-note" style="margin:10px 0 0;">${icon('info', 18)}<span>${escapeHtml(t('settings.letterhead.bankIbanNote'))}</span></div>`, s);
  }
  return '';
}

// ── Aktionen ──────────────────────────────────────────────
const EMPTY_ITEM = {
  contactEmails: () => ({ value: '', label: '' }),
  contactPhones: () => ({ value: '', label: '' }),
  legalResponsiblePersons: () => '',
  bankAccounts: () => ({ bankName: '', accountHolder: '', iban: '', bic: '' }),
};

// welche Felder gehen beim Speichern des Abschnitts an die API (partieller Patch)
const SECTION_FIELDS = {
  unternehmen: ['legalName', 'street', 'addressSuffix', 'postalCode', 'city', 'country', 'trade'],
  kontakt: ['contactEmails', 'contactPhones', 'website'],
  absender: ['senderLineShow', 'senderLineAddress'],
  rechtliches: ['legalResponsibleType', 'legalResponsiblePersons', 'uid', 'vatNumber', 'commercialRegisterNumber', 'commercialRegisterOffice'],
  bank: ['bankAccounts'],
};

function openSection(s, section) {
  const v = lh(s);
  const l = layout(s);
  const copy = {
    abstaende: () => ({ margins: { ...l.margins } }),
    unternehmen: () => ({ legalName: v.legalName, street: v.street, addressSuffix: v.addressSuffix, postalCode: v.postalCode, city: v.city, country: v.country || 'CH', trade: v.trade }),
    kontakt: () => ({ contactEmails: [...(v.contactEmails || [])], contactPhones: [...(v.contactPhones || [])], website: v.website }),
    absender: () => ({ senderLineShow: v.senderLineShow !== false, senderLineAddress: v.senderLineAddress }),
    logo: () => ({}),
    person: () => ({ contactPersons: (data(s).contactPersons || []).map((p) => ({ ...p })) }),
    rechtliches: () => ({ legalResponsibleType: v.legalResponsibleType, legalResponsiblePersons: [...(v.legalResponsiblePersons || [])], uid: v.uid, vatNumber: v.vatNumber, commercialRegisterNumber: v.commercialRegisterNumber, commercialRegisterOffice: v.commercialRegisterOffice }),
  }[section];
  s.lhEdit = { section, data: copy ? copy() : {} };
  if (section === 'bank') s.lhEdit.data = { bankAccounts: (v.bankAccounts || []).map((b) => ({ ...b })) };
}

// Schriftart im Select gewechselt → direkt speichern (kein Modal, wie bei plancraft)
export function handleLetterheadFontChange(ctx) {
  const { s, draw, reload } = ctx;
  if (!ctx.can('layout:write')) return;
  const font = document.getElementById('lh-font')?.value;
  if (!font || font === layout(s).font) return;
  saveLetterheadLayout({ font })
    .then(() => { loadLetterheadPreview(ctx); if (reload) reload(); else draw(); })
    .catch((err) => { s.error = err.message || t('common.saveFailed'); draw(); });
}

// Vorschaubilder der hinterlegten Hintergründe nachladen (Auth → Blob, wie beim Logo)
export function loadLetterheadBackgrounds(ctx) {
  const { s, draw } = ctx;
  const bg = layout(s).background || {};
  s.lhBgPreview = s.lhBgPreview || {};
  ['first', 'rest'].forEach((slot) => {
    if (!bg[slot] || s.lhBgPreview[slot]) return;
    fetchLetterheadBackgroundBlob(slot).then((url) => {
      if (url) { s.lhBgPreview[slot] = url; draw(); }
    }).catch(() => { /* kein Bild → Platzhalter bleibt */ });
  });
}

function dropPreviewBlob(s, slot) {
  if (s.lhBgPreview?.[slot]) { URL.revokeObjectURL(s.lhBgPreview[slot]); delete s.lhBgPreview[slot]; }
}

// Nach Upload/Löschen: Stammdaten neu holen, DANN Vorschaubild + Muster-Vorschau —
// beide brauchen das frische layout.background, sonst lädt der neue Bogen nie nach.
function refreshAfterBackgroundChange(ctx) {
  const { draw, reload } = ctx;
  return Promise.resolve(reload ? reload() : null).then(() => {
    loadLetterheadBackgrounds(ctx);
    loadLetterheadPreview(ctx);
    draw();
  });
}

// Datei-Auswahl für einen Hintergrund-Slot (verstecktes <input type=file>)
function pickBackgroundFile(slot, ctx) {
  const { s, draw, reload } = ctx;
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'application/pdf,image/png,image/jpeg';
  input.onchange = () => {
    const file = input.files && input.files[0];
    if (!file) return;
    s.lhBgError = ''; s.lhBgUploading = true; draw();
    uploadLetterheadBackground(slot, file)
      .then(() => {
        // altes Blob-Vorschaubild verwerfen, sonst zeigt die Karte den vorherigen Bogen
        dropPreviewBlob(s, slot);
        s.lhBgUploading = false;
        return refreshAfterBackgroundChange(ctx);
      })
      .catch((err) => { s.lhBgUploading = false; s.lhBgError = err.message || t('settings.letterhead.uploadFailed'); draw(); });
  };
  input.click();
}

export function handleLetterheadAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  if (!a || a.indexOf('lh-') !== 0) return false;
  if (a === 'lh-stop') return true;

  if (a === 'lh-tab') { s.lhTab = el.dataset.tab; draw(); return true; }
  if (a === 'lh-cancel') { s.lhEdit = null; s.logoError = ''; draw(); return true; }

  // ── Layout-Modus umschalten (speichert sofort) ──
  if (a === 'lh-mode') {
    if (!ctx.can('layout:write')) return true;
    const mode = el.value;
    if (mode === layout(s).mode) return true;
    saveLetterheadLayout({ mode })
      .then(() => { loadLetterheadPreview(ctx); if (reload) reload(); else draw(); })
      .catch((err) => { s.error = err.message || t('common.saveFailed'); draw(); });
    return true;
  }

  // ── PDF-Gestaltung der Offerte (speichert sofort) ──
  if (a === 'lh-doclayout') {
    if (!ctx.can('layout:write')) return true;
    const value = el.value;
    if (value === (data(s).documentLayout || 'STANDARD')) return true;
    // fetchMe: das Offerten-Formular liest das Layout aus dem Nutzer-Objekt —
    // ohne Nachladen zeigte es die Umzugs-Felder erst nach dem nächsten Login.
    saveDocumentLayout(value)
      .then(() => fetchMe().catch(() => null))
      .then(() => { if (reload) reload(); else draw(); })
      .catch((err) => { s.error = err.message || t('common.saveFailed'); draw(); });
    return true;
  }

  // ── Individueller Hintergrund ──
  if (a === 'lh-bg-pick') {
    if (!ctx.can('layout:write')) return true;
    pickBackgroundFile(el.dataset.slot, ctx);
    return true;
  }
  if (a === 'lh-bg-del') {
    if (!ctx.can('layout:write')) return true;
    const slot = el.dataset.slot;
    s.lhBgError = ''; draw();
    deleteLetterheadBackground(slot)
      .then(() => { dropPreviewBlob(s, slot); return refreshAfterBackgroundChange(ctx); })
      .catch((err) => { s.lhBgError = err.message || t('settings.letterhead.removeFailed'); draw(); });
    return true;
  }

  if (a.indexOf('lh-edit-') === 0) {
    if (!ctx.can('layout:write')) return true;
    const section = a.replace('lh-edit-', '');
    openSection(s, section);
    // Logo: gespeichertes Logo mit Auth als Blob holen (ein <img> auf die API-URL hätte keine Auth)
    if (section === 'logo' && !s.logoPreview && lh(s).logoUrl) {
      fetchCompanyLogoBlob().then((url) => { if (url) { s.logoPreview = url; draw(); } }).catch(() => { /* kein Logo */ });
    }
    draw();
    return true;
  }
  if (a === 'lh-toggle-sender') { syncFromDom(s); draw(); return true; }
  if (a === 'lh-add-item') {
    syncFromDom(s);
    const list = el.dataset.list;
    s.lhEdit.data[list] = [...(s.lhEdit.data[list] || []), EMPTY_ITEM[list]()];
    draw();
    return true;
  }
  if (a === 'lh-del-item') {
    syncFromDom(s);
    const list = el.dataset.list;
    const arr = [...(s.lhEdit.data[list] || [])];
    arr.splice(Number(el.dataset.index), 1);
    s.lhEdit.data[list] = arr;
    draw();
    return true;
  }
  if (a === 'lh-save') {
    if (!s.lhEdit || !ctx.can('layout:write')) return true;
    syncFromDom(s);
    const { section, data: d } = s.lhEdit;
    if (section === 'logo') { s.lhEdit = null; draw(); return true; } // Logo speichert beim Upload
    s.lhSaving = true; draw();
    // nach dem Speichern: Daten neu laden UND Vorschau neu rendern
    const done = () => { s.lhSaving = false; s.lhEdit = null; loadLetterheadPreview(ctx); if (reload) reload(); else draw(); };
    const fail = (err) => { s.lhSaving = false; s.error = err.message || t('common.saveFailed'); draw(); };
    // Layout liegt auf der Organization → eigener Endpunkt statt CompanyProfile-Patch
    if (section === 'abstaende') {
      const n = (v) => { const x = Number(v); return Number.isFinite(x) ? Math.min(100, Math.max(0, x)) : 0; };
      saveLetterheadLayout({ margins: { topFirst: n(d.margins.topFirst), topFollowing: n(d.margins.topFollowing), bottom: n(d.margins.bottom) } }).then(done).catch(fail);
    } else if (section === 'person') {
      saveContactPersons(d.contactPersons.map((p) => ({ id: p.id, email: p.email, phone: p.phone }))).then(done).catch(fail);
    } else {
      const patch = {};
      (SECTION_FIELDS[section] || []).forEach((k) => { patch[k] = d[k]; });
      saveLetterhead(patch).then(done).catch(fail);
    }
    return true;
  }
  return false;
}
